import React from "react";
import styles from "./about.module.css";
import TextHero from "../../COMPONENTS/TEXT_HERO";
import PageNav from "../../COMPONENTS/PAGE_NAV";
import Portrait from "../../COMPONENTS/PORTRAIT";
import Reveal from "../../COMPONENTS/ANIMATION/Reveal";
import { Button, Section, SectionIntro } from "../../COMPONENTS/UI";
import StatisticsSection from "../HOME_PAGE/SECTIONS/STATS";
import FinalCta from "../HOME_PAGE/SECTIONS/CTA";
import { aboutHero, advantage, leaders, mission, partnership, story } from "../../CONTENT/about";
import useDocumentMeta from "../../UTILS/HOOKS/useDocumentMeta";

const links = [
  { id: "about", label: "Our story" },
  { id: "advantage", label: "Our approach" },
  { id: "mission", label: "Mission" },
  { id: "executives", label: "Leadership" },
];

const Aboutpage = () => {
  useDocumentMeta({
    title: "About KUR Research | Clinical Research Site Network",
    description:
      "KUR Research is a leader in facilitating clinical research in urgent care centers and private practices, with eighteen locations and a diverse patient population.",
    path: "/about",
  });

  return (
    <>
      <TextHero
        id="about-hero-title"
        title={
          <>
            Countless possibilities to make <em>progress possible.</em>
          </>
        }
        lead={aboutHero.lead}
        actions={aboutHero.actions}
      >
        <ul className={styles.pillars} aria-label="What sets KUR apart">
          {aboutHero.pillars.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      </TextHero>
      <StatisticsSection />
      <PageNav links={links} cta={{ label: "Talk to KUR", href: "/contact" }} />

      <Section tone="light" id="about" labelledBy="story-title">
        <div className={styles.split}>
          <SectionIntro
            id="story-title"
            eyebrow="Our story"
            title="An integrated clinical research site network."
          />
          <Reveal className={styles.prose}>
            {story.map((p) => (
              <p key={p.slice(0, 24)}>{p}</p>
            ))}
          </Reveal>
        </div>
        <Reveal as="blockquote" className={styles.callout}>
          <p>{partnership}</p>
        </Reveal>
      </Section>

      <Section tone="cloud" id="advantage" labelledBy="advantage-title">
        <div className={styles.split}>
          <SectionIntro
            id="advantage-title"
            eyebrow="Our approach"
            title="Clear competitive advantages. True enterprise value."
          />
          <Reveal className={styles.prose}>
            {advantage.map((p) => (
              <p key={p.slice(0, 24)}>{p}</p>
            ))}
          </Reveal>
        </div>
      </Section>

      <Section tone="ink" id="mission" labelledBy="mission-title">
        <SectionIntro
          id="mission-title"
          eyebrow="Mission statement"
          title="One mission, four commitments."
        />
        <ol className={styles.mission}>
          {mission.map((m, i) => (
            <Reveal as="li" key={m.audience} delay={i * 0.1} className={styles.missionCard}>
              <span className={styles.missionIndex} aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </span>
              <img src={m.icon} alt="" className={styles.missionIcon} />
              <h3 className={styles.missionTitle}>{m.audience}</h3>
              <p className={styles.missionText}>{m.text}</p>
            </Reveal>
          ))}
        </ol>
      </Section>

      <Section tone="light" id="executives" labelledBy="leaders-title">
        <SectionIntro
          id="leaders-title"
          eyebrow="Leadership"
          title="The team behind KUR."
          lead="The leaders who built the KUR network and support every site, investigator and study."
        />
        <div className={styles.leaders}>
          {leaders.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1} className={styles.leader}>
              <Portrait src={p.image} alt={p.name} name={p.name} shape="tall" className={styles.portrait} />
              <h3 className={styles.name}>{p.name}</h3>
              <p className={styles.role}>{p.role}</p>
              <p className={styles.company}>{p.company}</p>
            </Reveal>
          ))}
        </div>
        <div className={styles.more}>
          <p>Meet the physicians who bring KUR research to their own communities.</p>
          <Button href="/network/doctors" variant="link" tone="light">
            Meet our investigators
          </Button>
        </div>
      </Section>

      <FinalCta />
    </>
  );
};

export default Aboutpage;
